import { Link } from "react-router-dom";
import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { CTASection } from "../components/CTASection";
import { RESOURCE_PAGES } from "../data/resourcePages";

const GROUP_ORDER = [
  "Decision Guides",
  "CPA & Accounting Resources",
  "Architecture & Engineering Resources",
  "Comparisons & FAQs",
];

export default function ResourcesHub() {
  const grouped = useMemo(() => {
    const acc = {};
    Object.entries(RESOURCE_PAGES).forEach(([slug, d]) => {
      (acc[d.group] = acc[d.group] || []).push({ slug, ...d });
    });
    return acc;
  }, []);

  return (
    <div data-testid="resources-page">
      <Seo
        title="IT & Cybersecurity Resources for Atlanta Firms | Zentryx Systems"
        description="Guides, comparisons, and educational resources on managed IT and cybersecurity for Atlanta CPA, accounting, architecture, and engineering firms."
        path="/resources"
      />
      <section className="pt-32 sm:pt-40 pb-16 px-5 sm:px-8 bg-mist">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Resources</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-charcoal leading-tight">
              Guides to smarter IT decisions.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Practical guides, comparisons, and answers to help Atlanta firms make confident technology and security decisions.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-24 px-5 sm:px-8">
        <div className="max-w-6xl mx-auto space-y-16">
          {GROUP_ORDER.filter((g) => grouped[g]).map((group) => (
            <Reveal key={group}>
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-charcoal mb-6">{group}</h2>
                <div className="grid md:grid-cols-2 gap-5">
                  {grouped[group].map((r) => (
                    <Link key={r.slug} to={`/resources/${r.slug}`}
                      data-testid={`resource-link-${r.slug}`}
                      className="group rounded-3xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/60 transition-all duration-300">
                      <h3 className="font-heading text-lg font-bold text-charcoal">{r.h1}</h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.metaDescription}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:gap-2.5 transition-all">
                        Read more <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <CTASection />
    </div>
  );
}
